/**
 * services/metrics.ts — Canonical pricing math.
 *
 * Single source of truth for break-even, overhead/hr, and target rate.
 * Pure function — no React, no Supabase, no side effects. Trivially testable.
 *
 * Consumers (must NOT duplicate this math):
 *  - contexts/SettingsContext.tsx     (live metrics on settings change)
 *  - components/WizardSidebar.tsx     (wizard right rail preview)
 *  - components/SetupWizard.tsx       (wizard summary card)
 *  - hooks/useEstimateForm.ts         (PricingSnapshot capture)
 */

import type { BusinessSettings, BusinessMetrics, Employee, OverheadItem, OverheadEquipment } from '../types';
import type { Clave } from '../i18n/textos';
import { effectiveBurdenPercent } from './burden';

// --- Thresholds used for warnings (industry baselines, not hard limits) ---
export const MIN_REASONABLE_BURDEN_PERCENT = 18;   // FICA + WC + benefits typically >= 20%
export const MAX_TARGET_MARGIN_PERCENT = 80;        // above this the formula explodes
export const MIN_REASONABLE_UTILIZATION = 1;        // 0% util means zero billable hours
export const MAX_REASONABLE_UTILIZATION = 100;

/* --- Techos de sensatez -----------------------------------------------------
 *
 * Había piso y no había techo, y por ahí se colaba el peor fallo del módulo:
 * un sueldo por hora tecleado como 1818 (era la cifra semanal) daba una tarifa
 * de 4.113 $/h, y una prima anual de seguro escrita en la casilla «cada mes»
 * daba 1.412 $/h. Ninguno de los dos sacaba un solo aviso: el contratista
 * perdía todas las licitaciones sin saber por qué.
 *
 * Son AVISOS, nunca bloqueos. Un dueño que se paga 150 $/h existe; 1.818 $/h no.
 * Las bandas van holgadas a propósito — sobra con cazar el error de dedo. */
export const WAGE_HOURLY_MIN = 7;        // por debajo del salario mínimo federal
export const WAGE_HOURLY_MAX = 150;
export const WAGE_SALARY_MIN = 15_000;
export const WAGE_SALARY_MAX = 500_000;
/** Un gasto suelto por encima de esto casi siempre es una anual metida en «mensual». */
export const OVERHEAD_LINE_MAX_ANNUAL = 200_000;
export const RATE_MIN_PLAUSIBLE = 25;
export const RATE_MAX_PLAUSIBLE = 400;

export type MetricsWarningSeverity = 'error' | 'warning';

export type MetricsWarningCode =
  | 'NO_BILLABLE_EMPLOYEES'      // error — billable hours = 0, formula returns $0
  | 'ZERO_OVERHEAD'              // warning — likely missing real overhead
  | 'BURDEN_TOO_LOW'             // warning — burden under industry floor
  | 'MARGIN_TOO_HIGH'            // error — margin above the ceiling
  | 'UTIL_OVER_100'              // error — single-factor or combined > 100
  | 'RATE_BELOW_WAGE'            // warning — break-even < avg wage (sanity check)
  | 'NO_EMPLOYEES'               // error — empty employees array
  | 'EQUIPMENT_NO_LIFE'          // warning — equipment item with invalid useful life
  | 'WAGE_IMPLAUSIBLE'           // error — wage outside a believable band for its pay type
  | 'OVERHEAD_LINE_IMPLAUSIBLE'  // error — one expense line dwarfs everything else
  | 'RATE_IMPLAUSIBLE'           // error — the resulting rate can't be right
  | 'DUPLICATE_FUEL'             // warning — fuel charged in overhead AND on equipment
  | 'DUPLICATE_INSURANCE_WC'     // warning — workers' comp inside burden AND in overhead
  | 'INVALID_INPUT';             // error — a field held something that isn't a number

export interface MetricsWarning {
  code: MetricsWarningCode;
  severity: MetricsWarningSeverity;
  /**
   * CLAVE del texto y sus valores, no el texto ya armado.
   *
   * Esto es un servicio de cálculo: no tiene hook ni idioma, y con el mensaje
   * escrito aquí los avisos salían en inglés dentro de una pantalla en español
   * («Forman effective burden is 15.0%…» encima de «Gastos fijos y precios»).
   * Quien los pinta —MetricsWarningsBanner— es el que traduce.
   */
  clave: Clave;
  vars?: Record<string, string | number>;
  /** DOM id of the input that triggered the warning (lets the UI scroll to it). */
  anchorId?: string;
}

export interface EmployeeBreakdown {
  id: string;
  name: string;
  role: string;
  headcount: number;         // people in this position (>= 1)
  isBillable: boolean;
  annualCost: number;        // grossPay + burden, for the WHOLE row
  billableHours: number;     // 0 for non-billable staff; row total
  effectiveUtilizationPercent: number; // for display
}

export interface OverheadBreakdown {
  id: string;
  name: string;
  annualAmount: number;
}

export interface MetricsBreakdown {
  employees: EmployeeBreakdown[];
  overheadItems: OverheadBreakdown[];
  totalDirectLaborCost: number;        // billable staff payroll + burden
  totalIndirectLaborCost: number;      // non-billable staff (rolls into overhead)
  totalOverheadFromExpenses: number;   // user-entered overhead items
  totalOverheadFromEquipment: number;  // depreciation + fuel + maintenance (Pass 3)
  totalOverhead: number;               // expenses + equipment + indirect labor
  totalBillableHours: number;
  warnings: MetricsWarning[];
}

// --- Helpers ---------------------------------------------------------------

const normalizeArray = <T>(data: unknown): T[] => {
  if (!data) return [];
  const raw = Array.isArray(data)
    ? data
    : typeof data === 'object'
      ? Object.values(data as Record<string, T>)
      : [];
  // Una fila `null` dentro del JSONB reventaba el `forEach` entero.
  return raw.filter((row): row is T => row != null && typeof row === 'object');
};

/**
 * Convierte a número lo que venga del JSONB.
 *
 * Todo esto sale de Postgres como JSON y no hay nada que garantice el tipo: un
 * importador o el asistente de IA pueden dejar `"1000"` en vez de `1000`. Con
 * texto, `+` concatena — `0 + "1000" + "200"` daba `"01000200"` y de ahí salían
 * puntos de equilibrio de nueve cifras, sin un solo aviso. `NaN` era peor: se
 * colaba silenciosamente porque `NaN > 0` es falso, así que las guardas de
 * división lo leían como «cero» en vez de como «inválido».
 *
 * Devuelve `null` cuando el valor no es convertible, para poder avisar en vez
 * de fingir un 0.
 */
function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Igual que `toNumber`, pero con valor por defecto para campos opcionales. */
function num(value: unknown, fallback = 0): number {
  return toNumber(value) ?? fallback;
}

/**
 * Effective utilization for an employee.
 *
 * Pass 1 (current): legacy single `utilizationPercent` field. Treat as combined.
 * Pass 2 (planned):  `availablePercent` × `billablePercent` / 100.
 *
 * This helper isolates that logic so Pass 2 is a one-line change.
 */
function getEffectiveUtilizationPercent(emp: Employee & { availablePercent?: number; billablePercent?: number }): number {
  const avail = toNumber(emp.availablePercent);
  const bill = toNumber(emp.billablePercent);
  if (avail !== null && bill !== null) return (avail * bill) / 100;
  return num(emp.utilizationPercent, 0);
}

/**
 * Is this employee billable? Pass 1 assumes every employee is billable;
 * Pass 2 reads the explicit `isBillable` flag.
 */
function getIsBillable(emp: Employee & { isBillable?: boolean }): boolean {
  if (typeof emp.isBillable === 'boolean') return emp.isBillable;
  return true; // legacy default
}

/**
 * How many people this row represents.
 *
 * The crew is modelled by POSITION, not by person: one row can be "Laborer × 3".
 * Absent/invalid values collapse to 1 so every pre-headcount record keeps its
 * exact previous math — this is what makes the migration lossless.
 */
function getHeadcount(emp: Employee & { headcount?: number }): number {
  const n = toNumber(emp.headcount);
  if (n === null || n < 1) return 1;
  return Math.floor(n);
}

/** Label for warnings: position first, falling back to a name, then a generic. */
function employeeLabel(emp: Employee): string {
  return emp.role || emp.name || 'Crew row';
}

// --- Main calc -------------------------------------------------------------

/**
 * Annualized cost contribution of one equipment item.
 *
 *   annualCost = purchasePrice / usefulLifeYears   // straight-line depreciation
 *              + fuel + maintenance + insurance
 *
 * Straight-line was chosen over declining-balance because SMB contractors
 * rarely have the data to use the latter accurately, and the simpler math
 * is easier to explain in the breakdown panel.
 */
export function calculateEquipmentAnnualCost(eq: OverheadEquipment): number {
  const life = eq.usefulLifeYears > 0 ? eq.usefulLifeYears : 0;
  const depreciation = life > 0 ? (eq.purchasePrice ?? 0) / life : 0;
  return depreciation + (eq.fuelAnnualCost ?? 0) + (eq.maintenanceAnnualCost ?? 0) + (eq.insuranceAnnualCost ?? 0);
}

export function calculateBusinessMetrics(settings: BusinessSettings): BusinessMetrics {
  const { season, pricing } = settings;
  const employees = normalizeArray<Employee>(settings.employees);
  const overhead = normalizeArray<OverheadItem>(settings.overhead);
  const equipment = normalizeArray<OverheadEquipment>(settings.equipment);

  const warnings: MetricsWarning[] = [];

  // --- A. Capacity ---
  const productionDays = (season?.weeksPerYear ?? 0) * (season?.daysPerWeek ?? 0);
  const seasonHours = productionDays * (season?.hoursPerDay ?? 0);

  // --- B. Labor (split into direct/indirect) ---
  let totalDirectLaborCost = 0;
  let totalIndirectLaborCost = 0;
  let totalBillableHours = 0;
  /* El sueldo medio se calcula por HORA PAGADA, no por cabeza.
   *
   * Antes solo sumaba filas por hora, así que un taller todo a salario daba
   * 0,00 $/h en pantalla junto a un equilibrio de 94 $ — y, peor, apagaba
   * `RATE_BELOW_WAGE`, que es la única comprobación de sensatez que tenía el
   * resultado, justo en los talleres con más probabilidad de equivocarse. */
  let directGrossPay = 0;
  let directPaidHours = 0;
  let totalGrossPayroll = 0;
  let totalBurdenCost = 0;
  /** Filas con burden por debajo del piso — se agrupan en UN aviso al final. */
  let lowBurdenRows = 0;
  let lowBurdenFirstAnchor: string | undefined;

  const employeeBreakdown: EmployeeBreakdown[] = [];

  employees.forEach((emp) => {
    const effectiveUtil = getEffectiveUtilizationPercent(emp);
    const isBillable = getIsBillable(emp);
    // One row = one POSITION held by `headcount` people. Every money and hours
    // figure below is a per-person figure multiplied by this at the end.
    const headcount = getHeadcount(emp);
    const label = employeeLabel(emp);

    // Sanity: combined utilization can't exceed 100%
    if (effectiveUtil > MAX_REASONABLE_UTILIZATION) {
      warnings.push({
        code: 'UTIL_OVER_100',
        severity: 'error',
        clave: 'metricas.aviso.utilizacionAlta',
        vars: { quien: label, pct: effectiveUtil.toFixed(0) },
        anchorId: `emp-${emp.id}`,
      });
    }

    const cappedUtil = Math.min(Math.max(effectiveUtil, 0), MAX_REASONABLE_UTILIZATION);

    const empSeasonHours = seasonHours;

    // Cost for ONE person in this position (hourly scales with empSeasonHours;
    // salary is annual flat), then scaled by headcount for the row total.
    const wage = toNumber(emp.wage);
    if (wage === null) {
      warnings.push({
        code: 'INVALID_INPUT',
        severity: 'error',
        clave: 'metricas.aviso.datoInvalido',
        vars: { campo: 'wage', quien: label },
        anchorId: `emp-${emp.id}`,
      });
    }
    const safeWage = wage ?? 0;

    /* Techo de sensatez por tipo de pago. Un sueldo por hora de 1.818 es la
     * cifra de la SEMANA metida en la casilla equivocada; 221 es la del DÍA. */
    const isHourly = emp.payType === 'Hourly';
    const wageMin = isHourly ? WAGE_HOURLY_MIN : WAGE_SALARY_MIN;
    const wageMax = isHourly ? WAGE_HOURLY_MAX : WAGE_SALARY_MAX;
    if (safeWage > 0 && (safeWage < wageMin || safeWage > wageMax)) {
      warnings.push({
        code: 'WAGE_IMPLAUSIBLE',
        severity: 'error',
        clave: isHourly ? 'metricas.aviso.sueldoHoraRaro' : 'metricas.aviso.sueldoAnualRaro',
        vars: { quien: label, monto: safeWage.toLocaleString(), min: wageMin.toLocaleString(), max: wageMax.toLocaleString() },
        anchorId: `emp-${emp.id}`,
      });
    }

    const grossPayPerPerson = isHourly ? safeWage * empSeasonHours : safeWage;
    const grossPay = grossPayPerPerson * headcount;

    // Effective burden % — reads from the Burden Builder if enabled, otherwise
    // falls back to the employee's typed laborBurdenPercent. Computed on the
    // PER-PERSON wage: health insurance is a $/month/employee figure, so
    // converting it against a whole crew's payroll would divide it by headcount.
    const burdenPct = effectiveBurdenPercent(settings, { ...emp, annualHours: empSeasonHours });
    const burden = grossPay * (burdenPct / 100);
    const annualCost = grossPay + burden;

    /* Burden por debajo del piso — se CUENTA, no se avisa aquí.
     *
     * Antes salía un aviso por fila. Con 96 filas del corpus sentadas en un
     * valor semilla viejo (15) el banner llegaba a enseñar catorce renglones
     * idénticos en un solo taller, y un banner que grita en la mitad de las
     * pantallas es un banner que se ignora — que es exactamente cómo se
     * publicó una tarifa de 4.113 $/h sin que nadie la viera. Un solo aviso
     * con el número de filas. */
    if (burdenPct < MIN_REASONABLE_BURDEN_PERCENT) {
      lowBurdenRows += 1;
      if (!lowBurdenFirstAnchor) {
        lowBurdenFirstAnchor = settings.burden?.useBuilder ? 'burden-builder' : `emp-${emp.id}`;
      }
    }

    // Billable hours for the whole row (zero for non-billable staff)
    const billableHours = isBillable ? empSeasonHours * (cappedUtil / 100) * headcount : 0;

    totalGrossPayroll += grossPay;
    totalBurdenCost += burden;

    if (isBillable) {
      totalDirectLaborCost += annualCost;
      totalBillableHours += billableHours;
    } else {
      // Non-billable staff cost rolls into overhead, not labor rate.
      totalIndirectLaborCost += annualCost;
    }

    if (isBillable) {
      // Coste por hora PAGADA, salariados incluidos: un dueño a 60.000 $ sobre
      // 1.400 horas de temporada vale 42,86 $/h, no 0.
      directGrossPay += grossPay;
      directPaidHours += empSeasonHours * headcount;
    }

    employeeBreakdown.push({
      id: emp.id,
      name: emp.role || emp.name || 'Unnamed',
      role: emp.role,
      headcount,
      isBillable,
      annualCost,
      billableHours,
      effectiveUtilizationPercent: cappedUtil,
    });
  });

  // --- C. Overhead from expenses ---
  const overheadBreakdown: OverheadBreakdown[] = [];
  let totalOverheadFromExpenses = 0;
  /** Gastos con pinta de combustible — para cazar el doble conteo con maquinaria. */
  let fuelFromExpenses = 0;
  /** Gastos con pinta de seguro — el workers' comp ya va dentro del burden. */
  let insuranceFromExpenses = 0;

  overhead.forEach((item) => {
    const amount = toNumber(item.amount);
    if (amount === null && item.amount !== undefined && item.amount !== null) {
      warnings.push({
        code: 'INVALID_INPUT',
        severity: 'error',
        clave: 'metricas.aviso.datoInvalido',
        vars: { campo: 'amount', quien: item.name || '—' },
        anchorId: 'overhead',
      });
    }
    const annualAmount = item.frequency === 'Monthly' ? (amount ?? 0) * 12 : (amount ?? 0);
    totalOverheadFromExpenses += annualAmount;

    // Una sola línea por encima del techo casi siempre es una cifra ANUAL
    // tecleada en un renglón que viene por defecto en «cada mes».
    if (annualAmount > OVERHEAD_LINE_MAX_ANNUAL) {
      warnings.push({
        code: 'OVERHEAD_LINE_IMPLAUSIBLE',
        severity: 'error',
        clave: 'metricas.aviso.gastoRaro',
        vars: {
          nombre: item.name || '—',
          anual: Math.round(annualAmount).toLocaleString(),
          frecuencia: item.frequency,
        },
        anchorId: 'overhead',
      });
    }

    const name = (item.name || '').toLowerCase();
    if (/fuel|gas|gasolina|diesel|diésel|combustible/.test(name)) fuelFromExpenses += annualAmount;
    if (/insur|seguro|liability|wc\b|workers|compensa/.test(name)) insuranceFromExpenses += annualAmount;

    overheadBreakdown.push({
      id: item.id,
      name: item.name || '(unnamed)',
      annualAmount,
    });
  });

  // --- D. Overhead from equipment ---
  let totalOverheadFromEquipment = 0;
  let fuelFromEquipment = 0;
  equipment.forEach((eq) => {
    if (num(eq.usefulLifeYears) <= 0 && num(eq.purchasePrice) > 0) {
      warnings.push({
        code: 'EQUIPMENT_NO_LIFE',
        severity: 'warning',
        clave: 'metricas.aviso.sinVidaUtil',
        vars: { nombre: eq.name || '—' },
        anchorId: `eq-${eq.id}`,
      });
    }
    fuelFromEquipment += num(eq.fuelAnnualCost);
    totalOverheadFromEquipment += calculateEquipmentAnnualCost(eq);
  });

  /* Combustible guardado en la máquina — dato heredado.
   *
   * La casilla de gasolina por máquina se retiró: ahora el combustible va en un
   * solo renglón de gastos fijos. Lo que ya estaba guardado SE SIGUE SUMANDO,
   * porque tres talleres solo lo tienen ahí y dejar de contarlo les bajaría el
   * costo de golpe — y un costo que baja solo es el peor error posible: cotizan
   * por debajo sin enterarse.
   *
   * El aviso les dice que lo muevan. A los diez que lo tienen en los dos sitios
   * además les está inflando la tarifa: uno metió los mismos 4.800 $ en ambos y
   * su equilibrio sale 11,4 % alto. No se toca el dato de nadie. */
  if (fuelFromEquipment > 0) {
    warnings.push({
      code: 'DUPLICATE_FUEL',
      severity: 'warning',
      clave: fuelFromExpenses > 0 ? 'metricas.aviso.combustibleDoble' : 'metricas.aviso.combustibleEnMaquina',
      vars: {
        gastos: Math.round(fuelFromExpenses).toLocaleString(),
        maquinaria: Math.round(fuelFromEquipment).toLocaleString(),
      },
      anchorId: 'overhead',
    });
  }

  /* Workers' comp contado dos veces: el porcentaje de cargas ya lo incluye
   * (así está documentado en industryPricingDefaults y así lo arma el
   * constructor de cargas), y encima la lista sugerida pide teclear la factura
   * del seguro, que lo trae dentro. Mide +6,6 % en la tarifa. */
  const anyBurden = employeeBreakdown.length > 0 && totalBurdenCost > 0;
  if (insuranceFromExpenses > 0 && anyBurden) {
    warnings.push({
      code: 'DUPLICATE_INSURANCE_WC',
      severity: 'warning',
      clave: 'metricas.aviso.seguroDoble',
      vars: { gastos: Math.round(insuranceFromExpenses).toLocaleString() },
      anchorId: 'overhead',
    });
  }

  // --- E. Totals ---
  const totalOverhead = totalOverheadFromExpenses + totalOverheadFromEquipment + totalIndirectLaborCost;
  const totalAnnualCost = totalDirectLaborCost + totalOverhead;

  // --- F. Rates ---
  const overheadPerManHour = totalBillableHours > 0 ? totalOverhead / totalBillableHours : 0;
  const breakEvenRate = totalBillableHours > 0 ? totalAnnualCost / totalBillableHours : 0;

  /* Un solo techo, y se RECORTA en vez de devolver 0.
   *
   * Había cuatro topes distintos —80 en la constante, 100 en el error, 80 en la
   * casilla, 60 en los deslizadores— y un quinto sitio sin ninguno. Además,
   * pasado el tope la función devolvía 0: una tarifa de 0 $/h se lee como «no
   * hay datos», no como «el margen es imposible». Ahora avisa y sigue
   * calculando con el margen recortado, que es un número con el que se puede
   * trabajar mientras se corrige. */
  const rawMargin = num(pricing?.targetMarginPercent, 0);
  const targetMarginPercent = Math.min(Math.max(rawMargin, 0), MAX_TARGET_MARGIN_PERCENT);
  if (rawMargin > MAX_TARGET_MARGIN_PERCENT) {
    warnings.push({
      code: 'MARGIN_TOO_HIGH',
      severity: 'error',
      clave: 'metricas.aviso.margenImposible',
      vars: { pct: rawMargin, tope: MAX_TARGET_MARGIN_PERCENT },
      anchorId: 'pricing-margin',
    });
  }
  const targetMarginDecimal = targetMarginPercent / 100;
  const targetHourlyRate = breakEvenRate > 0 ? breakEvenRate / (1 - targetMarginDecimal) : 0;

  // --- G. Top-level warnings ---
  if (employees.length === 0) {
    warnings.push({
      code: 'NO_EMPLOYEES',
      severity: 'error',
      clave: 'metricas.aviso.sinEmpleados',
      anchorId: 'employees',
    });
  } else if (totalBillableHours === 0) {
    warnings.push({
      code: 'NO_BILLABLE_EMPLOYEES',
      severity: 'error',
      clave: 'metricas.aviso.sinHorasCobrables',
      anchorId: 'employees',
    });
  }

  /* «No tienes gastos fijos» tiene que mirar el TOTAL, no solo la lista de
   * gastos: un taller con una flota de 80.000 $ y ningún renglón escrito sí
   * tiene overhead, y le salía el aviso igual. */
  if (totalOverhead === 0 && employees.length > 0) {
    warnings.push({
      code: 'ZERO_OVERHEAD',
      severity: 'warning',
      clave: 'metricas.aviso.sinGastos',
      anchorId: 'overhead',
    });
  }

  if (lowBurdenRows > 0) {
    warnings.push({
      code: 'BURDEN_TOO_LOW',
      severity: 'warning',
      clave: 'metricas.aviso.burdenBajo',
      vars: { count: lowBurdenRows, piso: MIN_REASONABLE_BURDEN_PERCENT },
      anchorId: lowBurdenFirstAnchor,
    });
  }

  const avgHourlyWage = directPaidHours > 0 ? directGrossPay / directPaidHours : 0;
  if (avgHourlyWage > 0 && breakEvenRate > 0 && breakEvenRate < avgHourlyWage) {
    warnings.push({
      code: 'RATE_BELOW_WAGE',
      severity: 'warning',
      clave: 'metricas.aviso.equilibrioBajoSueldo',
      vars: { equilibrio: breakEvenRate.toFixed(2), sueldo: avgHourlyWage.toFixed(2) },
    });
  }

  /* Última red: la tarifa que sale. Aunque cada campo pase su banda, la
   * combinación puede dar un disparate — y es el único número que el
   * contratista lleva a la calle. */
  if (breakEvenRate > 0 && (breakEvenRate < RATE_MIN_PLAUSIBLE || breakEvenRate > RATE_MAX_PLAUSIBLE)) {
    warnings.push({
      code: 'RATE_IMPLAUSIBLE',
      severity: 'error',
      clave: breakEvenRate > RATE_MAX_PLAUSIBLE ? 'metricas.aviso.tarifaAlta' : 'metricas.aviso.tarifaBaja',
      vars: {
        tarifa: breakEvenRate.toFixed(2),
        min: RATE_MIN_PLAUSIBLE,
        max: RATE_MAX_PLAUSIBLE,
      },
    });
  }

  const avgLaborBurdenPercent = totalGrossPayroll > 0 ? (totalBurdenCost / totalGrossPayroll) * 100 : 0;

  return {
    productionDays,
    seasonHours,
    totalBillableHours,
    totalAnnualCost,
    totalOverhead,
    overheadPerManHour,
    breakEvenRate,
    targetHourlyRate,
    avgHourlyWage,
    avgLaborBurdenPercent,
    breakdown: {
      employees: employeeBreakdown,
      overheadItems: overheadBreakdown,
      totalDirectLaborCost,
      totalIndirectLaborCost,
      totalOverheadFromExpenses,
      totalOverheadFromEquipment,
      totalOverhead,
      totalBillableHours,
      warnings,
    },
  };
}
